import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { leadService, brokerService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const Container = styled.div`
  padding: 20px;
  width: 100%;
  overflow-x: hidden;
  min-height: calc(100vh - 80px); // Account for header height
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  color: #333;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #007bff;
          color: white;
          &:hover { background: #0056b3; }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
          &:hover { background: #545b62; }
        `;
    }
  }}
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 4px;
  margin-right: 15px;
`;

const ViewButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  
  ${props => props.active ? `
    background: #007bff;
    color: white;
  ` : `
    background: transparent;
    color: #6c757d;
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

// Kanban Styles
const KanbanBoard = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 20px;
  min-height: 600px;
  width: 100%;
  
  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const KanbanColumn = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  min-width: 300px;
  max-width: 300px;
  flex: 0 0 300px; /* Prevent column from shrinking */
  display: flex;
  flex-direction: column;
  height: fit-content;
  min-height: 200px;
`;

const ColumnHeader = styled.div<{ color: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  background: ${props => props.color};
  color: white;
  border-radius: 6px;
  font-weight: 600;
`;

const ColumnTitle = styled.div`
  font-size: 16px;
`;

const ColumnCount = styled.div`
  background: rgba(255,255,255,0.2);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
`;

const ClientCard = styled.div`
  background: white;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: grab;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    transform: translateY(-2px);
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const ClientName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
`;

const ClientEmail = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
`;

const ClientPhone = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
`;

const ClientType = styled.div<{ type: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'buyer':
        return 'background: #e7f3ff; color: #0066cc;';
      case 'renter':
        return 'background: #fff2e7; color: #cc6600;';
      case 'owner':
        return 'background: #e7ffe7; color: #006600;';
      case 'broker':
        return 'background: #f3e7ff; color: #6600cc;';
      default:
        return 'background: #f0f0f0; color: #666;';
    }
  }}
`;

const ClientActions = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 10px;
`;

const ClientActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  
  ${props => {
    switch (props.variant) {
      case 'edit':
        return `
          background: #28a745;
          color: white;
          &:hover { background: #218838; }
        `;
      case 'delete':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
          &:hover { background: #545b62; }
        `;
    }
  }}
`;

// Table Styles (mantendo os existentes)
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Table = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  background: white;
`;

const Th = styled.th`
  padding: 15px 12px;
  text-align: left;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: top;
`;


const ClientTypeBadge = styled.span<{ type: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'buyer':
        return 'background: #e7f3ff; color: #0066cc;';
      case 'renter':
        return 'background: #fff2e7; color: #cc6600;';
      case 'owner':
        return 'background: #e7ffe7; color: #006600;';
      case 'broker':
        return 'background: #f3e7ff; color: #6600cc;';
      case 'doorman':
        return 'background: #ffe7f3; color: #cc0066;';
      case 'investor':
        return 'background: #ffffe7; color: #cccc00;';
      default:
        return 'background: #f0f0f0; color: #666;';
    }
  }}
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  
  ${props => {
    switch (props.variant) {
      case 'edit':
        return `
          background: #28a745;
          color: white;
          &:hover { background: #218838; }
        `;
      case 'delete':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
          &:hover { background: #545b62; }
        `;
    }
  }}
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

// Pipeline Modal Styles
const PipelineCard = styled.div<{ isDefault: boolean }>`
  background: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.isDefault ? '#28a745' : '#dee2e6'};
`;

const PipelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const PipelineName = styled.h4`
  margin: 0;
  color: #333;
`;

const DefaultBadge = styled.span`
  background: #28a745;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const PipelineDescription = styled.p`
  color: #666;
  margin: 10px 0;
  font-size: 14px;
`;

const StagesContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 15px 0;
  flex-wrap: wrap;
`;

const StageChip = styled.div<{ color: string }>`
  background: ${props => props.color};
  color: white;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
`;

const PipelineActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 15px;
`;

const SmallButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #007bff;
          color: white;
          &:hover { background: #0056b3; }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
          &:hover { background: #545b62; }
        `;
    }
  }}
`;

const StagesSection = styled.div`
  margin-top: 20px;
`;

const StageItem = styled.div<{ isDragging?: boolean }>`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 10px;
  border: 1px solid #dee2e6;
  cursor: grab;
  transition: all 0.2s;
  
  ${props => props.isDragging && `
    opacity: 0.5;
    transform: rotate(5deg);
  `}
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const StageRow = styled.div`
  display: grid;
  grid-template-columns: auto 2fr 1fr 1fr auto;
  gap: 10px;
  align-items: center;
`;

const PositionIndicator = styled.div`
  background: #007bff;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const ColorPicker = styled.input`
  width: 40px;
  height: 30px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

const RemoveStageButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #c82333;
  }
`;

const AddStageButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;
  
  &:hover {
    background: #218838;
  }
`;

const BrokerSelect = styled.select`
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 11px;
  margin-top: 5px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const BrokerInfo = styled.div`
  font-size: 10px;
  color: #007bff;
  margin-top: 3px;
  font-weight: 500;
`;

interface Client {
  id: number;
  name: string;
  email: string;
  phone: string;
  client_type: string;
  interest_type: string;
  notes: string;
  cpf_cnpj: string;
  profession: string;
  company: string;
  creci: string;
  created_at: string;
  pipeline_stage_id?: number | null;
  assigned_broker_id?: number;
  assigned_broker?: {
    id: number;
    name: string;
    email: string;
    creci: string;
  };
}

interface ClientTypeOption {
  value: string;
  label: string;
  description: string;
}

interface Stats {
  total: number;
  por_estagio: {
    [key: string]: number;
  };
  por_tipo: {
    compradores: number;
    locatarios: number;
    proprietarios: number;
    corretores: number;
  };
}

interface KanbanColumnData {
  stage_id: number;
  stage_name: string;
  stage_color: string;
  clients: Client[];
  client_count: number;
}

interface KanbanData {
  pipeline: {
    id: number;
    name: string;
    description: string;
    color: string;
  };
  columns: KanbanColumnData[];
  total_clients: number;
}

interface Pipeline {
  id: number;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  color: string;
  stages: PipelineStage[];
}

interface PipelineStage {
  id?: number;
  name: string;
  description: string;
  color: string;
  position: number;
  is_final: boolean;
  lead_status: string;
}

interface Broker {
  id: number;
  name: string;
  email: string;
  creci: string;
  specialty?: string;
  is_active: boolean;
  is_team_leader: boolean;
}

interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  client_type: string;
  interest_type: string;
  notes: string;
  cpf_cnpj: string;
  profession: string;
  company: string;
  creci: string;
  assigned_broker_id: string;
  pipeline_stage_id: number | null;
  status?: string;
}

const AdminLeads: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientTypes, setClientTypes] = useState<ClientTypeOption[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [kanbanData, setKanbanData] = useState<KanbanData | null>(null);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [draggedStageIndex, setDraggedStageIndex] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    client_type: '',
    assigned_broker_id: ''
  });
  const { showNotification } = useNotification();

  // Form state
  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    email: '',
    phone: '',
    client_type: 'buyer',
    interest_type: 'information',
    notes: '',
    cpf_cnpj: '',
    profession: '',
    company: '',
    creci: '',
    assigned_broker_id: '',
    pipeline_stage_id: null
  });

  // Pipeline form state
  const [pipelineFormData, setPipelineFormData] = useState({
    name: '',
    description: '',
    color: '#007bff',
    stages: [
      { name: 'Novo', description: 'Cliente recém cadastrado', color: '#17a2b8', position: 1, is_final: false, lead_status: 'new' },
      { name: 'Contatado', description: 'Cliente já foi contatado', color: '#ffc107', position: 2, is_final: false, lead_status: 'contacted' },
      { name: 'Qualificado', description: 'Cliente qualificado', color: '#fd7e14', position: 3, is_final: false, lead_status: 'qualified' },
      { name: 'Fechado', description: 'Negócio fechado', color: '#28a745', position: 4, is_final: true, lead_status: 'closed_won' },
      { name: 'Perdido', description: 'Cliente perdido', color: '#dc3545', position: 5, is_final: true, lead_status: 'closed_lost' }
    ]
  });

  const defaultStatusOptions = [
    { value: 'new', label: 'Novo' },
    { value: 'contacted', label: 'Contatado' },
    { value: 'qualified', label: 'Qualificado' },
    { value: 'proposal_sent', label: 'Proposta Enviada' },
    { value: 'negotiating', label: 'Negociando' },
    { value: 'closed_won', label: 'Fechado - Ganho' },
    { value: 'closed_lost', label: 'Fechado - Perdido' },
    { value: 'follow_up', label: 'Acompanhamento' },
    { value: 'on_hold', label: 'Em Espera' }
  ];

  const loadBrokers = useCallback(async () => {
    try {
      const brokersData = await brokerService.getActive();
      setBrokers(brokersData);
    } catch (error) {
      console.error('Erro ao carregar corretores:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      await loadBrokers(); // Carregar corretores sempre
      
      if (viewMode === 'kanban') {
        const [typesData, statsData, kanbanBoardData, pipelinesData] = await Promise.all([
          leadService.getClientTypes(),
          leadService.getStats(),
          leadService.getKanbanBoard(),
          leadService.getPipelines()
        ]);
        
        setClientTypes(typesData.client_types);
        setStats(statsData);
        setKanbanData(kanbanBoardData);
        setPipelines(pipelinesData);
      } else {
        const [clientsData, typesData, statsData] = await Promise.all([
          leadService.getAll(),
          leadService.getClientTypes(),
          leadService.getStats()
        ]);
        
        setClients(clientsData);
        setClientTypes(typesData.client_types);
        setStats(statsData);
      }
    } catch (error) {
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  }, [viewMode, showNotification, loadBrokers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      client_type: 'buyer',
      interest_type: 'information',
      status: 'new',
      notes: '',
      cpf_cnpj: '',
      profession: '',
      company: '',
      creci: '',
      assigned_broker_id: '',
      pipeline_stage_id: null
    });
    setIsModalOpen(true);
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      client_type: client.client_type || 'buyer',
      interest_type: client.interest_type || 'information',
      notes: client.notes || '',
      cpf_cnpj: client.cpf_cnpj || '',
      profession: client.profession || '',
      company: client.company || '',
      creci: client.creci || '',
      assigned_broker_id: client.assigned_broker_id?.toString() || '',
      pipeline_stage_id: client.pipeline_stage_id || null
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      // Validação básica
      if (!formData.name.trim()) {
        showNotification('Nome é obrigatório', 'error');
        return;
      }
      
      if (!formData.email.trim()) {
        showNotification('Email é obrigatório', 'error');
        return;
      }
      
      // Validação de email simples
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        showNotification('Email deve ter um formato válido', 'error');
        return;
      }
      
      // Preparar dados para envio - converter campos vazios para null
      const dataToSend = {
        ...formData,
        assigned_broker_id: formData.assigned_broker_id === '' ? null : parseInt(formData.assigned_broker_id)
      };
      
      // Se estivermos criando um novo cliente e não há pipeline_stage_id, 
      // definir o primeiro estágio do pipeline padrão
      if (!editingClient && !dataToSend.pipeline_stage_id) {
        if (kanbanData && kanbanData.columns && kanbanData.columns.length > 0) {
          dataToSend.pipeline_stage_id = kanbanData.columns[0].stage_id;
          console.log('Atribuindo estágio padrão do kanban:', kanbanData.columns[0].stage_id, kanbanData.columns[0].stage_name);
        } else {
          console.warn('Nenhum estágio disponível no kanban para atribuir ao novo cliente');
          console.log('kanbanData disponível:', kanbanData);
        }
      }
      
      console.log('Salvando cliente com dados:', dataToSend);
      
      let savedClient;
      if (editingClient) {
        console.log('Atualizando cliente existente:', editingClient.id);
        savedClient = await leadService.update(editingClient.id, dataToSend);
        console.log('Cliente atualizado:', savedClient);
        showNotification('Cliente atualizado com sucesso', 'success');
      } else {
        console.log('Criando novo cliente');
        savedClient = await leadService.create(dataToSend);
        console.log('Cliente criado:', savedClient);
        showNotification('Cliente criado com sucesso', 'success');
      }
      
      setIsModalOpen(false);
      
      // Recarregar dados e verificar se o cliente aparece no kanban
      await loadData();
      
      console.log('Dados kanban após salvar:', kanbanData);
    } catch (error: any) {
      console.error('Erro detalhado ao salvar cliente:', error);
      showNotification(`Erro ao salvar cliente: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await leadService.delete(id);
        showNotification('Cliente excluído com sucesso', 'success');
        loadData();
      } catch (error) {
        showNotification('Erro ao excluir cliente', 'error');
      }
    }
  };

  const handleMoveClient = async (clientId: number, fromStageId: number, toStageId: number) => {
    try {
      await leadService.moveClientInKanban({
        client_id: clientId,
        from_stage_id: fromStageId,
        to_stage_id: toStageId,
        notes: `Movido via kanban`
      });
      
      showNotification('Cliente movido com sucesso', 'success');
      loadData();
    } catch (error) {
      showNotification('Erro ao mover cliente', 'error');
    }
  };

  // Pipeline management functions
  const handleCreatePipeline = () => {
    setEditingPipeline({ id: 0 } as Pipeline); // Usar id: 0 para indicar criação
    setPipelineFormData({
      name: '',
      description: '',
      color: '#007bff',
      stages: [
        { name: 'Novo', description: 'Cliente recém cadastrado', color: '#17a2b8', position: 1, is_final: false, lead_status: 'new' },
        { name: 'Contatado', description: 'Cliente já foi contatado', color: '#ffc107', position: 2, is_final: false, lead_status: 'contacted' },
        { name: 'Qualificado', description: 'Cliente qualificado', color: '#fd7e14', position: 3, is_final: false, lead_status: 'qualified' },
        { name: 'Fechado', description: 'Negócio fechado', color: '#28a745', position: 4, is_final: true, lead_status: 'closed_won' },
        { name: 'Perdido', description: 'Cliente perdido', color: '#dc3545', position: 5, is_final: true, lead_status: 'closed_lost' }
      ]
    });
  };

  const handleEditPipeline = (pipeline: Pipeline) => {
    setEditingPipeline(pipeline);
    setPipelineFormData({
      name: pipeline.name,
      description: pipeline.description,
      color: pipeline.color,
      stages: pipeline.stages.map(stage => ({
        ...(stage.id && { id: stage.id }),
        name: stage.name,
        description: stage.description,
        color: stage.color,
        position: stage.position,
        is_final: stage.is_final,
        lead_status: stage.lead_status
      }))
    });
  };

  const handleSavePipeline = async () => {
    if (!pipelineFormData.name.trim()) {
      showNotification('Nome do pipeline é obrigatório', 'error');
      return;
    }

    if (pipelineFormData.stages.length === 0) {
      showNotification('Pipeline deve ter pelo menos um estágio', 'error');
      return;
    }

    try {
      const pipelineData = {
        name: pipelineFormData.name.trim(),
        description: pipelineFormData.description,
        color: pipelineFormData.color,
        stages: pipelineFormData.stages
      };

      console.log('Salvando pipeline:', pipelineData);
      console.log('Editing pipeline:', editingPipeline);
      console.log('Condição de edição:', editingPipeline && editingPipeline.id && editingPipeline.id > 0);

      if (editingPipeline && editingPipeline.id && editingPipeline.id > 0) {
        console.log('Atualizando pipeline existente:', editingPipeline.id);
        await leadService.updatePipeline(editingPipeline.id, pipelineData);
        showNotification('Pipeline atualizado com sucesso', 'success');
      } else {
        console.log('Criando novo pipeline');
        const result = await leadService.createPipeline(pipelineData);
        console.log('Pipeline criado:', result);
        showNotification('Pipeline criado com sucesso', 'success');
      }
      
      setEditingPipeline(null);
      loadData();
    } catch (error) {
      console.error('Erro ao salvar pipeline:', error);
      showNotification('Erro ao salvar pipeline', 'error');
    }
  };

  const handleDeletePipeline = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este pipeline?')) {
      try {
        await leadService.deletePipeline(id);
        showNotification('Pipeline excluído com sucesso', 'success');
        loadData();
      } catch (error) {
        showNotification('Erro ao excluir pipeline', 'error');
      }
    }
  };

  const handleSetDefaultPipeline = async (id: number) => {
    try {
      await leadService.setDefaultPipeline(id);
      showNotification('Pipeline definido como padrão', 'success');
      loadData();
    } catch (error) {
      showNotification('Erro ao definir pipeline padrão', 'error');
    }
  };

  const addPipelineStage = async () => {
    const newStage = {
      name: '',
      description: '',
      color: '#6c757d',
      position: pipelineFormData.stages.length + 1,
      is_final: false,
      lead_status: 'new'
    };

    // Se estamos editando um pipeline existente, criar o estágio no backend
    if (editingPipeline && editingPipeline.id && editingPipeline.id > 0) {
      try {
        const createdStage = await leadService.createPipelineStage(editingPipeline.id, newStage);
        
        // Adicionar o estágio criado ao estado local
        setPipelineFormData({
          ...pipelineFormData,
          stages: [...pipelineFormData.stages, createdStage]
        });
        
        showNotification('Estágio adicionado com sucesso', 'success');
        loadData(); // Recarregar dados
      } catch (error) {
        console.error('Erro ao adicionar estágio:', error);
        showNotification('Erro ao adicionar estágio', 'error');
      }
    } else {
      // Se é um pipeline novo, apenas adicionar ao estado local
      setPipelineFormData({
        ...pipelineFormData,
        stages: [...pipelineFormData.stages, newStage]
      });
    }
  };

  const removePipelineStage = async (index: number) => {
    const stageToRemove = pipelineFormData.stages[index];
    
    // Se estamos editando um pipeline existente e o estágio tem ID, remover do backend
    if (editingPipeline && editingPipeline.id && editingPipeline.id > 0 && (stageToRemove as any).id) {
      try {
        await leadService.deletePipelineStage((stageToRemove as any).id);
        showNotification('Estágio removido com sucesso', 'success');
        loadData(); // Recarregar dados
      } catch (error) {
        console.error('Erro ao remover estágio:', error);
        showNotification('Erro ao remover estágio', 'error');
        return; // Não remover do estado local se falhou no backend
      }
    }

    // Remover do estado local
    const newStages = pipelineFormData.stages.filter((_, i) => i !== index);
    const reorderedStages = newStages.map((stage, i) => ({
      ...stage,
      position: i + 1
    }));
    setPipelineFormData({
      ...pipelineFormData,
      stages: reorderedStages
    });
  };

  const updatePipelineStage = async (index: number, field: string, value: any) => {
    const newStages = [...pipelineFormData.stages];
    const updatedStage = {
      ...newStages[index],
      [field]: value
    };
    newStages[index] = updatedStage;

    // Atualizar estado local primeiro
    setPipelineFormData({
      ...pipelineFormData,
      stages: newStages
    });

    // Se estamos editando um pipeline existente e o estágio tem ID, atualizar no backend
    if (editingPipeline && editingPipeline.id && editingPipeline.id > 0 && (updatedStage as any).id) {
      try {
        // Debounce para evitar muitas chamadas
        clearTimeout((window as any).stageUpdateTimeout);
        (window as any).stageUpdateTimeout = setTimeout(async () => {
          await leadService.updatePipelineStage((updatedStage as any).id, updatedStage);
          console.log('Estágio atualizado automaticamente');
        }, 1000);
      } catch (error) {
        console.error('Erro ao atualizar estágio:', error);
      }
    }
  };

  // Drag & Drop functions for stages
  const handleStageDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedStageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
  };

  const handleStageDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleStageDrop = async (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedStageIndex === null || draggedStageIndex === dropIndex) {
      setDraggedStageIndex(null);
      return;
    }

    const newStages = [...pipelineFormData.stages];
    const draggedStage = newStages[draggedStageIndex];
    
    // Remove o estágio da posição original
    newStages.splice(draggedStageIndex, 1);
    
    // Insere na nova posição
    newStages.splice(dropIndex, 0, draggedStage);
    
    // Atualiza as posições
    const reorderedStages = newStages.map((stage, index) => ({
      ...stage,
      position: index + 1
    }));

    setPipelineFormData({
      ...pipelineFormData,
      stages: reorderedStages
    });

    // Se estamos editando um pipeline existente, salvar a reordenação
    if (editingPipeline && editingPipeline.id && editingPipeline.id > 0) {
      saveStageOrder(reorderedStages);
    }

    setDraggedStageIndex(null);
  };

  const saveStageOrder = async (stages: any[]) => {
    try {
      const stagePositions: Array<{id: number, position: number}> = [];
      stages.forEach(stage => {
        if ((stage as any).id !== undefined) {
          stagePositions.push({
            id: (stage as any).id,
            position: stage.position
          });
        }
      });

      if (stagePositions.length > 0 && editingPipeline?.id) {
        console.log('Salvando ordem dos estágios:', stagePositions);
        await leadService.reorderPipelineStages(editingPipeline.id, stagePositions);
        showNotification('Ordem dos estágios atualizada', 'success');
        
        // Recarregar dados para garantir sincronização
        setTimeout(() => {
          loadData();
        }, 500);
      }
    } catch (error) {
      console.error('Erro ao reordenar estágios:', error);
      showNotification('Erro ao reordenar estágios', 'error');
    }
  };

  const handleStageDragEnd = () => {
    setDraggedStageIndex(null);
  };

  const getClientTypeLabel = (type: string) => {
    const clientType = clientTypes.find(ct => ct.value === type);
    return clientType ? clientType.label : type;
  };


  const filteredClients = clients.filter(client => {
    const matchesSearch = !filters.search || 
      client.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      client.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      (client.phone && client.phone.includes(filters.search));
    
    const matchesType = !filters.client_type || client.client_type === filters.client_type;
    const matchesBroker = !filters.assigned_broker_id || 
      (filters.assigned_broker_id === 'unassigned' ? !client.assigned_broker_id : 
       client.assigned_broker_id?.toString() === filters.assigned_broker_id);
    
    return matchesSearch && matchesType && matchesBroker;
  });

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, client: Client) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      clientId: client.id,
      fromStageId: client.pipeline_stage_id || 1
    }));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, toStageId: number) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    
    if (data.fromStageId && data.fromStageId !== toStageId) {
      handleMoveClient(data.clientId, data.fromStageId, toStageId);
    }
  };

  const handleAssignBroker = async (clientId: number, brokerId: number | null) => {
    try {
      await leadService.update(clientId, {
        assigned_broker_id: brokerId
      });
      
      showNotification('Corretor atribuído com sucesso', 'success');
      loadData();
    } catch (error) {
      showNotification('Erro ao atribuir corretor', 'error');
    }
  };

  const filteredKanbanData = useMemo(() => {
    if (!kanbanData || !filters.assigned_broker_id) {
      return kanbanData;
    }

    const filteredColumns = kanbanData.columns.map(column => {
      const filteredClients = column.clients.filter(client => {
        if (filters.assigned_broker_id === 'unassigned') {
          return !client.assigned_broker_id;
        }
        return client.assigned_broker_id?.toString() === filters.assigned_broker_id;
      });

      return {
        ...column,
        clients: filteredClients,
        client_count: filteredClients.length
      };
    });

    const totalClients = filteredColumns.reduce((total, column) => total + column.client_count, 0);

    return {
      ...kanbanData,
      columns: filteredColumns,
      total_clients: totalClients
    };
  }, [kanbanData, filters.assigned_broker_id]);

  if (loading) {
    return <Container><div>Carregando...</div></Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Gestão de Clientes</Title>
      </Header>

      <ButtonGroup style={{ marginBottom: '20px' }}>
        <ViewToggle>
          <ViewButton 
            active={viewMode === 'list'} 
            onClick={() => setViewMode('list')}
          >
            📋 Lista
          </ViewButton>
          <ViewButton 
            active={viewMode === 'kanban'} 
            onClick={() => setViewMode('kanban')}
          >
            📊 Kanban
          </ViewButton>
        </ViewToggle>
        {viewMode === 'kanban' && (
          <Button onClick={() => setIsPipelineModalOpen(true)}>
            ⚙️ Configurar Pipeline
          </Button>
        )}
        <Button variant="primary" onClick={handleCreate}>
          + Novo Cliente
        </Button>
      </ButtonGroup>

      {stats && (
        <StatsSection>
          <StatCard>
            <StatNumber>{stats.total}</StatNumber>
            <StatLabel>Total de Clientes</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.por_estagio?.novo || 0}</StatNumber>
            <StatLabel>Novos</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.por_tipo.proprietarios}</StatNumber>
            <StatLabel>Proprietários</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.por_tipo.compradores}</StatNumber>
            <StatLabel>Compradores</StatLabel>
          </StatCard>
        </StatsSection>
      )}

      {viewMode === 'list' && (
        <>
          <FilterSection>
            <FilterGroup>
              <Label>Buscar</Label>
              <Input
                type="text"
                placeholder="Nome, email ou telefone..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
              />
            </FilterGroup>
            
            <FilterGroup>
              <Label>Tipo de Cliente</Label>
              <Select
                value={filters.client_type}
                onChange={(e) => setFilters({...filters, client_type: e.target.value})}
              >
                <option value="">Todos os tipos</option>
                {clientTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FilterGroup>
            

            <FilterGroup>
              <Label>Corretor Atribuído</Label>
              <Select
                value={filters.assigned_broker_id}
                onChange={(e) => setFilters({...filters, assigned_broker_id: e.target.value})}
              >
                <option value="">Todos os corretores</option>
                <option value="unassigned">Sem corretor atribuído</option>
                {brokers.map(broker => (
                  <option key={broker.id} value={broker.id}>
                    {broker.name} - {broker.creci}
                  </option>
                ))}
              </Select>
            </FilterGroup>
          </FilterSection>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Cliente</Th>
                  <Th>Contato</Th>
                  <Th>Tipo</Th>
                  <Th>Corretor</Th>
                  <Th>Profissão</Th>
                  <Th>Observações</Th>
                  <Th>Ações</Th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.id}>
                    <Td>
                      <div style={{ fontWeight: 'bold' }}>{client.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {client.email}
                      </div>
                    </Td>
                    <Td>
                      <div>{client.phone}</div>
                      {client.cpf_cnpj && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          CPF/CNPJ: {client.cpf_cnpj}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <ClientTypeBadge type={client.client_type || ''}>
                        {getClientTypeLabel(client.client_type)}
                      </ClientTypeBadge>
                    </Td>
                    <Td>
                      {client.assigned_broker && (
                        <BrokerInfo>
                          👤 {client.assigned_broker.name}
                        </BrokerInfo>
                      )}
                    </Td>
                    <Td>
                      <div>{client.profession}</div>
                      {client.company && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {client.company}
                        </div>
                      )}
                      {client.creci && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          CRECI: {client.creci}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {client.notes}
                      </div>
                    </Td>
                    <Td>
                      <ActionButton variant="edit" onClick={() => handleEdit(client)}>
                        Editar
                      </ActionButton>
                      <ActionButton variant="delete" onClick={() => handleDelete(client.id)}>
                        Excluir
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </>
      )}

      {viewMode === 'kanban' && (
        <FilterSection>
          <FilterGroup>
            <Label>Filtrar por Corretor</Label>
            <Select
              value={filters.assigned_broker_id}
              onChange={(e) => setFilters({...filters, assigned_broker_id: e.target.value})}
            >
              <option value="">Todos os corretores</option>
              <option value="unassigned">Sem corretor atribuído</option>
              {brokers.map(broker => (
                <option key={broker.id} value={broker.id}>
                  {broker.name} - {broker.creci}
                </option>
              ))}
            </Select>
          </FilterGroup>
        </FilterSection>
      )}

      {viewMode === 'kanban' && filteredKanbanData && (
        <KanbanBoard>
          {filteredKanbanData.columns.map((column) => (
            <KanbanColumn
              key={column.stage_id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.stage_id)}
            >
              <ColumnHeader color={column.stage_color}>
                <ColumnTitle>{column.stage_name}</ColumnTitle>
                <ColumnCount>{column.client_count}</ColumnCount>
              </ColumnHeader>
              
              {column.clients.map((client) => (
                <ClientCard
                  key={client.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, client)}
                >
                  <ClientName>{client.name}</ClientName>
                  <ClientEmail>{client.email}</ClientEmail>
                  <ClientPhone>{client.phone}</ClientPhone>
                  
                  {client.client_type && (
                    <ClientType type={client.client_type}>
                      {getClientTypeLabel(client.client_type)}
                    </ClientType>
                  )}

                  {/* Seletor de Corretor */}
                  <BrokerSelect
                    value={client.assigned_broker_id || ''}
                    onChange={(e) => {
                      const brokerId = e.target.value ? parseInt(e.target.value) : null;
                      handleAssignBroker(client.id, brokerId);
                    }}
                    onClick={(e) => e.stopPropagation()} // Evita drag ao clicar no select
                  >
                    <option value="">Sem corretor atribuído</option>
                    {brokers.map(broker => (
                      <option key={broker.id} value={broker.id}>
                        {broker.name} - {broker.creci}
                      </option>
                    ))}
                  </BrokerSelect>

                  {client.assigned_broker && (
                    <BrokerInfo>
                      👤 {client.assigned_broker.name}
                    </BrokerInfo>
                  )}
                  
                  <ClientActions>
                    <ClientActionButton variant="edit" onClick={() => handleEdit(client)}>
                      ✏️
                    </ClientActionButton>
                    <ClientActionButton variant="delete" onClick={() => handleDelete(client.id)}>
                      🗑️
                    </ClientActionButton>
                  </ClientActions>
                </ClientCard>
              ))}
            </KanbanColumn>
          ))}
        </KanbanBoard>
      )}

      {/* Pipeline Configuration Modal */}
      <Modal isOpen={isPipelineModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Configurar Pipelines</ModalTitle>
            <CloseButton onClick={() => setIsPipelineModalOpen(false)}>×</CloseButton>
          </ModalHeader>

          <div style={{ marginBottom: '20px' }}>
            <Button variant="primary" onClick={handleCreatePipeline}>
              + Novo Pipeline
            </Button>
          </div>

          {pipelines.map((pipeline) => (
            <PipelineCard key={pipeline.id} isDefault={pipeline.is_default}>
              <PipelineHeader>
                <div>
                  <PipelineName>{pipeline.name}</PipelineName>
                  {pipeline.is_default && <DefaultBadge>Padrão</DefaultBadge>}
                </div>
              </PipelineHeader>
              
              <PipelineDescription>{pipeline.description}</PipelineDescription>
              
              <StagesContainer>
                {pipeline.stages.map((stage, index) => (
                  <StageChip key={stage.id || `stage-${index}`} color={stage.color}>
                    {stage.name}
                  </StageChip>
                ))}
              </StagesContainer>
              
              <PipelineActions>
                <SmallButton onClick={() => handleEditPipeline(pipeline)}>
                  Editar
                </SmallButton>
                {!pipeline.is_default && (
                  <SmallButton onClick={() => handleSetDefaultPipeline(pipeline.id)}>
                    Definir como Padrão
                  </SmallButton>
                )}
                {!pipeline.is_default && (
                  <SmallButton variant="danger" onClick={() => handleDeletePipeline(pipeline.id)}>
                    Excluir
                  </SmallButton>
                )}
              </PipelineActions>
            </PipelineCard>
          ))}

          {editingPipeline !== null && (
            <div style={{ marginTop: '30px', borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
                              <h3>{editingPipeline && editingPipeline.id ? 'Editar Pipeline' : 'Novo Pipeline'}</h3>
              
              <FormGroup>
                <FormLabel>Nome do Pipeline *</FormLabel>
                <FormInput
                  type="text"
                  value={pipelineFormData.name}
                  onChange={(e) => setPipelineFormData({...pipelineFormData, name: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Descrição</FormLabel>
                <FormTextarea
                  value={pipelineFormData.description}
                  onChange={(e) => setPipelineFormData({...pipelineFormData, description: e.target.value})}
                  placeholder="Descrição do pipeline..."
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Cor do Pipeline</FormLabel>
                <ColorPicker
                  type="color"
                  value={pipelineFormData.color}
                  onChange={(e) => setPipelineFormData({...pipelineFormData, color: e.target.value})}
                />
              </FormGroup>

              <StagesSection>
                <FormLabel>Estágios do Pipeline (arraste para reordenar)</FormLabel>
                {pipelineFormData.stages.map((stage, index) => (
                  <StageItem 
                    key={index}
                    draggable
                    isDragging={draggedStageIndex === index}
                    onDragStart={(e) => handleStageDragStart(e, index)}
                    onDragOver={handleStageDragOver}
                    onDrop={(e) => handleStageDrop(e, index)}
                    onDragEnd={handleStageDragEnd}
                  >
                    <StageRow>
                      <PositionIndicator>{index + 1}</PositionIndicator>
                      <FormInput
                        type="text"
                        placeholder="Nome do estágio"
                        value={stage.name}
                        onChange={(e) => updatePipelineStage(index, 'name', e.target.value)}
                      />
                      <FormSelect
                        value={stage.lead_status}
                        onChange={(e) => updatePipelineStage(index, 'lead_status', e.target.value)}
                      >
                        {defaultStatusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </FormSelect>
                      <ColorPicker
                        type="color"
                        value={stage.color}
                        onChange={(e) => updatePipelineStage(index, 'color', e.target.value)}
                      />
                      {pipelineFormData.stages.length > 1 && (
                        <RemoveStageButton onClick={() => removePipelineStage(index)}>
                          Remover
                        </RemoveStageButton>
                      )}
                    </StageRow>
                    <FormInput
                      type="text"
                      placeholder="Descrição do estágio"
                      value={stage.description}
                      onChange={(e) => updatePipelineStage(index, 'description', e.target.value)}
                      style={{ marginTop: '10px' }}
                    />
                  </StageItem>
                ))}
                
                <AddStageButton onClick={addPipelineStage}>
                  + Adicionar Estágio
                </AddStageButton>
              </StagesSection>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <Button variant="primary" onClick={handleSavePipeline}>
                  {editingPipeline && editingPipeline.id ? 'Atualizar' : 'Criar'} Pipeline
                </Button>
                <Button onClick={() => setEditingPipeline(null)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Client Modal */}
      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingClient ? 'Editar Cliente' : 'Novo Cliente'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
          </ModalHeader>

          <FormRow>
            <FormGroup>
              <FormLabel>Nome *</FormLabel>
              <FormInput
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Email *</FormLabel>
              <FormInput
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <FormLabel>Telefone</FormLabel>
              <FormInput
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>CPF/CNPJ</FormLabel>
              <FormInput
                type="text"
                value={formData.cpf_cnpj}
                onChange={(e) => setFormData({...formData, cpf_cnpj: e.target.value})}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <FormLabel>Tipo de Cliente</FormLabel>
              <FormSelect
                value={formData.client_type}
                onChange={(e) => setFormData({...formData, client_type: e.target.value})}
              >
                {clientTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>
            
          </FormRow>

          <FormRow>
            <FormGroup>
              <FormLabel>Profissão</FormLabel>
              <FormInput
                type="text"
                value={formData.profession}
                onChange={(e) => setFormData({...formData, profession: e.target.value})}
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel>Empresa</FormLabel>
              <FormInput
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <FormLabel>CRECI (para corretores)</FormLabel>
            <FormInput
              type="text"
              value={formData.creci}
              onChange={(e) => setFormData({...formData, creci: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Corretor Responsável</FormLabel>
            <FormSelect
              value={formData.assigned_broker_id}
              onChange={(e) => setFormData({...formData, assigned_broker_id: e.target.value})}
            >
              <option value="">Nenhum corretor atribuído</option>
              {brokers.map(broker => (
                <option key={broker.id} value={broker.id}>
                  {broker.name} - {broker.creci} {broker.is_team_leader ? '(Líder)' : ''}
                </option>
              ))}
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>Observações</FormLabel>
            <FormTextarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Observações sobre o cliente..."
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="primary" onClick={handleSave}>
              {editingClient ? 'Atualizar' : 'Criar'} Cliente
            </Button>
            <Button onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminLeads; 
